import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { contactLogService, ContactLogResponseDto } from '../../../services/contactLogService';
import { tagService, TagDto } from '../../../services/tagService';
import Tag from '../../../components/Tag';
import TagAssignmentModal from '../../../components/TagAssignmentModal';
import ContactLogForm from './Form';
import { formatDateTimeUk, formatDateUk } from '../../../dateFormat';
import DeleteImpactModal from '../../../components/DeleteImpactModal';

const ContactLogDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = searchParams.get('edit') === 'true';
  const returnPropertyId = (() => {
    const raw = searchParams.get('propertyId');
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const navigateBack = () => {
    if (returnPropertyId != null) {
      navigate(`/Property Hub/Property/${returnPropertyId}`);
      return;
    }
    navigate('/Property Hub/Contact Logs');
  };

  const [contactLog, setContactLog] = useState<ContactLogResponseDto | null>(null);
  const [tags, setTags] = useState<TagDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState<{ attachments: number; tags: number; calendar: number } | null>(null);

  const loadContactLog = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const [logData, tagData] = await Promise.all([
        contactLogService.getContactLog(parseInt(id)),
        tagService.getTagLogsByEntity('ContactLog', parseInt(id)),
      ]);
      setContactLog(logData);
      setTags(tagData);
    } catch (err: any) {
      setError(err.message || 'Failed to load contact log');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id && !isEdit) {
      loadContactLog();
    }
  }, [id, isEdit, loadContactLog]);

  // If edit mode, show form instead
  if (isEdit) {
    return <ContactLogForm />;
  }

  const handleTagAdded = async () => {
    if (!id) return;
    try {
      const tagData = await tagService.getTagLogsByEntity('ContactLog', parseInt(id));
      setTags(tagData);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh tags');
    }
  };

  const handleRemoveTag = async (tagLogId: number) => {
    if (!window.confirm('Are you sure you want to remove this tag?')) return;

    try {
      await tagService.deleteTagLog(tagLogId);
      setTags(tags.filter(t => t.tagLogId !== tagLogId));
    } catch (err: any) {
      setError(err.message || 'Failed to remove tag');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    setUploading(true);
    setError(null);

    try {
      await contactLogService.addAttachment(parseInt(id), file);
      loadContactLog(); // Reload to get updated attachments
      e.target.value = ''; // Reset file input
    } catch (err: any) {
      setError(err.message || 'Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      await contactLogService.deleteAttachment(attachmentId);
      loadContactLog();
    } catch (err: any) {
      setError(err.message || 'Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (attachmentId: number) => {
    try {
      setError(null);
      const { blob, fileName } = await contactLogService.downloadAttachment(attachmentId);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download attachment');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const confirmDeleteContactLog = async () => {
    if (!id) return;
    try {
      setDeleteProcessing(true);
      await contactLogService.deleteContactLog(parseInt(id, 10));
      navigateBack();
    } catch (err: any) {
      setError(err.message || 'Failed to delete contact log');
    } finally {
      setDeleteProcessing(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!contactLog) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Contact log not found</p>
        <button
          onClick={navigateBack}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          {returnPropertyId != null ? 'Property overview' : 'Back to Contact Logs'}
        </button>
      </div>
    );
  }

  return (
    <div>
      <DeleteImpactModal
        isOpen={showDeleteModal}
        title="Delete Contact Log?"
        subjectLabel="this contact log"
        impacts={[
          { label: 'Calendar appointments', count: deleteImpact?.calendar ?? 0 },
          { label: 'Tags', count: deleteImpact?.tags ?? 0 },
          { label: 'Attachments', count: deleteImpact?.attachments ?? 0 },
        ]}
        confirmText="Delete contact log"
        isProcessing={deleteProcessing}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteContactLog}
      />
      <div className="flex justify-between items-center mb-6">
        <div>
          <button
            onClick={navigateBack}
            className="text-gray-600 hover:text-gray-900 mb-2"
          >
            ← {returnPropertyId != null ? 'Property overview' : 'Back to Contact Logs'}
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Contact Log Details</h2>
        </div>
        <div className="space-x-2">
          <button
            onClick={() => {
              const q = new URLSearchParams({ edit: 'true' });
              if (returnPropertyId != null) q.set('propertyId', String(returnPropertyId));
              navigate(`/Property Hub/Contact Logs/${id}?${q.toString()}`);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Edit
          </button>
          <button
            onClick={async () => {
              try {
                const impact = await contactLogService.getDeleteImpact(parseInt(id!, 10));
                setDeleteImpact({
                  attachments: impact.attachmentCount,
                  tags: impact.tagCount,
                  calendar: impact.calendarAppointmentCount,
                });
                setShowDeleteModal(true);
              } catch (err: any) {
                setError(err.message || 'Failed to load delete impact.');
              }
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contact Log Details */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Contact Details</h3>
          <dl className="grid grid-cols-1 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Contact Date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDateUk(contactLog.contactDate)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Property</dt>
              <dd className="mt-1 text-sm text-gray-900">{contactLog.propertyName}</dd>
            </div>
            {contactLog.tenantName && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Tenant</dt>
                <dd className="mt-1 text-sm text-gray-900">{contactLog.tenantName}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Contact Log Type</dt>
              <dd className="mt-1 text-sm text-gray-900">{contactLog.contactLogTypeName}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Subject</dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">{contactLog.subject}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Notes</dt>
              <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{contactLog.notes}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created Date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDateTimeUk(contactLog.createdDate)}
              </dd>
            </div>
          </dl>
        </div>

        {/* Attachments and Tags */}
        <div className="space-y-6">
          {/* Attachments */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Attachments</h3>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Attachment
              </label>
              <input
                type="file"
                onChange={handleFileUpload}
                disabled={uploading}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {uploading && <p className="mt-2 text-sm text-gray-500">Uploading...</p>}
            </div>

            {contactLog.attachments.length === 0 ? (
              <p className="text-sm text-gray-500">No attachments</p>
            ) : (
              <div className="space-y-2">
                {contactLog.attachments.map((attachment) => (
                  <div
                    key={attachment.attachmentId}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{attachment.fileName}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(attachment.fileSize)} • {formatDateUk(attachment.createdDate)}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        title={(attachment.fileSize ?? 0) > 0 ? 'Download attachment' : 'No file payload is stored for this attachment'}
                        onClick={() => handleDownloadAttachment(attachment.attachmentId)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Download
                      </button>
                      <button
                        onClick={() => handleDeleteAttachment(attachment.attachmentId)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Tags</h3>
              <button
                onClick={() => setShowTagModal(true)}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add Tag
              </button>
            </div>

            {tags.length === 0 ? (
              <p className="text-sm text-gray-500">No tags assigned</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Tag key={tag.tagLogId} tag={tag} onRemove={handleRemoveTag} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tag Assignment Modal */}
      {id && (
        <TagAssignmentModal
          isOpen={showTagModal}
          onClose={() => setShowTagModal(false)}
          entityType="ContactLog"
          entityId={parseInt(id)}
          existingTagTypeIds={tags.map(t => t.tagTypeId)}
          onTagAdded={handleTagAdded}
        />
      )}
    </div>
  );
};

export default ContactLogDetail;
